<template lang="pug" src="./notifications.pug"></template>
<style lang="scss" src="./notifications.scss" scoped></style>

<script>
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';

import config from '@/config.json';

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        filters: {
            filterBy: 'all',
            brandIds: [],
            optionClassIds: [],
            callSigns: '',
            startDate: null,
            endDate: null
        },
        notification: {
            title: '',
            body: '',
            image: ''
        },
        startDateModal: false,
        endDateModal: false,
        driverStatuses: [...config.driverStatuses, 'offline'],
        showCallSigns: false,
        sentModal: false,
        disableLoadDrivers: false,
        disableSentNotification: false,
        result: null,
        dataLoaded: false
    }),
    validations: {
        notification: {
            title: {
                required
            },
            body: {
                required,
                maxLength: function() {
                    return this.notification.body.length <= this.imageFilled;
                }
            }
        }
    },
    computed: {
        ...mapGetters(['userBrandId']),
        ...mapState({
            brands: state => state.data.brands,
            optionClasses: state => state.data.optionClasses,
            drivers: state => state.notifications.drivers
        }),
        selectAllBrands() {
            return this.filters.brandIds.length === this.brands.length;
        },
        selectSomeBrands() {
            return this.filters.brandIds.length > 0 && !this.selectAllBrands;
        },
        selectAllOptionClasses() {
            return this.filters.optionClassIds.length === this.optionClasses.length;
        },
        selectSomeOptionClasses() {
            return this.filters.optionClassIds.length > 0 && !this.selectAllOptionClasses;
        },
        callSignsIcon() {
            return this.showCallSigns ? 'mdi-eye-off' : 'mdi-eye';
        },
        filtersData() {
            return {
                brandIds: JSON.stringify(this.filters.brandIds),
                optionClassIds: JSON.stringify(this.filters.optionClassIds),
                filterBy: this.filters.filterBy,
                callSigns: this.filters.callSigns,
                startDate: this.filters.startDate,
                endDate: this.filters.endDate
            };
        },
        resultErrors() {
            return Object.entries(this.result.errors).map(([key, value]) => ({ key, value }));
        },
        imageFilled() {
            // If image field was filled we limit notification body to 50 symbols,
            // because when image send - text was overlaid
            return this.notification.image ? 50 : 255;
        },
        driversCount() {
            if (!Object.keys(this.drivers).length) {
                return 0;
            }

            return this.drivers.count;
        }
    },
    created() {
        this.$store.dispatch('data/getBrands').then(() => {
            if (!this.userBrandId) {
                this.filters.brandIds = this.brands.map(it => it.id);
            } else {
                this.filters.brandIds = [this.userBrandId];
            }

            this.getDrivers(true);
        });

        this.$store.dispatch('data/getOptionClasses');
    },
    methods: {
        toggleBrands() {
            this.$nextTick(() => {
                if (this.selectAllBrands) {
                    this.filters.brandIds = [];

                    this.$store.commit('notifications/SET_DRIVER_COMMISSIONS', []);
                } else {
                    this.filters.brandIds = this.brands.map(it => it.id);
                }
            });
        },

        toggleOptionClasses() {
            this.$nextTick(() => {
                if (this.selectAllOptionClasses) {
                    this.filters.optionClassIds = [];
                } else {
                    this.filters.optionClassIds = this.optionClasses.map(it => it.id);
                }
            });
        },

        toggleShowCallSigns() {
            this.showCallSigns = !this.showCallSigns;
        },

        getIcon(selectAll, selectSome) {
            if (selectAll) {
                return 'mdi-close-box';
            }

            if (selectSome) {
                return 'mdi-minus-box';
            }

            return 'mdi-checkbox-blank-outline';
        },

        async getDrivers(firstLoad = false) {
            this.disableLoadDrivers = true;

            await this.$store.dispatch('notifications/getDriversWithTokens', this.filtersData);

            if (!firstLoad) {
                setTimeout(() => {
                    this.disableLoadDrivers = false;
                }, 1000);
            } else {
                this.disableLoadDrivers = false;
            }

            this.dataLoaded = true;
        },

        async sentNotification() {
            if (this.disableSentNotification) {
                return;
            }

            this.disableSentNotification = true;

            const res = await this.$store.dispatch('notifications/sendNotification', {
                ...this.filtersData,
                data: this.notification
            });
            if (res) {
                this.result = res;
                this.clearNotification();
                this.$nextTick(() => {
                    this.$refs.resultCard.scrollIntoView({ behavior: "smooth" });
                });
            } else {
                this.sentModal = false;
            }

            setTimeout(() => {
                this.disableSentNotification = false;
            }, 1000);
        },

        validNotification() {
            if (this.$v.notification.$invalid) {
                this.$v.notification.$touch();

                return this.$nextTick(() => this.focusFirstErrorInput());
            }

            this.sentModal = true;
        },

        clearNotification() {
            this.notification = {
                title: '',
                body: '',
                image: ''
            };
            this.sentModal = false;

            this.$v.notification.$reset();
        }
    }
};
</script>
